import { Security, Key, Smartphone } from '@mui/icons-material';
import {
  Alert,
  Box,
  Card,
  CardContent,
  _Container,
  Typography,
  Button,
  Grid,
  Paper,
} from '@mui/material';
import React, { useCallback, useState } from 'react';
import { Helmet } from 'react-helmet';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { saveNextPath } from '../../AuthActions';
import { getNextPath, getOrganization } from '../../AuthReducer';
import TwoFactorSetup from '../../components/TwoFactorSetup/TwoFactorSetup';
import PasskeySetup from '../../components/PasskeySetup/PasskeySetup';
import LayoutFocused from '../../../App/components/LayoutFocused/LayoutFocused';

const messages = defineMessages({
  title: {
    id: 'TwoFactorSetupPage.Title',
    defaultMessage: 'Set Up Two-Factor Authentication - Promoter Ninja',
  },
  enforcedMessage: {
    id: 'TwoFactorSetupPage.EnforcedMessage',
    defaultMessage:
      'Your organization "{organizationName}" requires two-factor authentication for all users. Please complete the setup to continue.',
  },
  methodSelectionTitle: {
    id: 'TwoFactorSetupPage.MethodSelectionTitle',
    defaultMessage: 'Choose Your 2FA Method',
  },
  methodSelectionDescription: {
    id: 'TwoFactorSetupPage.MethodSelectionDescription',
    defaultMessage:
      'Select either two-factor authentication (TOTP) or passkeys for enhanced security.',
  },
  totpOption: {
    id: 'TwoFactorSetupPage.TotpOption',
    defaultMessage: 'Two-Factor Authentication (TOTP)',
  },
  totpDescription: {
    id: 'TwoFactorSetupPage.TotpDescription',
    defaultMessage:
      'Use an authenticator app like Google Authenticator, Authy, or Microsoft Authenticator.',
  },
  passkeyOption: {
    id: 'TwoFactorSetupPage.PasskeyOption',
    defaultMessage: 'Passkeys',
  },
  passkeyDescription: {
    id: 'TwoFactorSetupPage.PasskeyDescription',
    defaultMessage: 'Use biometric authentication or device PIN for seamless, secure access.',
  },
});

function TwoFactorSetupPage() {
  const { formatMessage } = useIntl();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const nextPath = useSelector(getNextPath);
  const organization = useSelector(getOrganization);

  const redirectPath = location.state?.nextPath || nextPath;

  const [selectedMethod, setSelectedMethod] = useState(null);
  const [setupOpen, setSetupOpen] = useState(false);

  const handleComplete = useCallback(() => {
    // Clear any stored next path
    dispatch(saveNextPath(null));

    // Redirect to the intended destination or dashboard
    const destination = redirectPath || '/';
    navigate(destination, { replace: true });
  }, [navigate, redirectPath, dispatch]);

  const handleError = useCallback(error => {
    console.error('2FA setup error:', error);
    // Stay on the same page to allow retry
  }, []);

  const handleClose = useCallback(() => {
    setSetupOpen(false);
  }, []);

  const handleMethodSelect = useCallback(method => {
    setSelectedMethod(method);
    setSetupOpen(true);
  }, []);

  const _handleBackToMethodSelection = useCallback(() => {
    setSetupOpen(false);
    setSelectedMethod(null);
  }, []);

  // Show method selection
  return (
    <LayoutFocused sx={{ maxWidth: 'md', textAlign: 'left' }}>
      <Helmet>
        <title>{formatMessage(messages.title)}</title>
      </Helmet>

      <Box
        sx={{
          mt: 4,
          mb: 4,
          maxWidth: 'md',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Card elevation={1}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 2 }}>
              <Security sx={{ mr: 2, color: 'info.main' }} />
              <Typography variant="h4" component="h1" gutterBottom>
                <FormattedMessage
                  id="TwoFactorSetupPage.SetupRequired"
                  defaultMessage="Two-Factor Authentication Required"
                />
              </Typography>
            </Box>

            <Alert severity="info">
              <FormattedMessage
                id="TwoFactorSetupPage.EnforcedInfo"
                defaultMessage="<b>{organizationName}</b> requires two-factor authentication for all users. <br></br>Please complete the setup to continue."
                values={{
                  organizationName: organization.name,
                  b: chunks => <strong>{chunks}</strong>,
                }}
              />
            </Alert>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 3 }}>
              <FormattedMessage
                id="TwoFactorSetupPage.EnforcedComplete"
                defaultMessage="Please complete the setup to continue."
              />
            </Typography>

            <Typography variant="h5" sx={{ mb: 2 }}>
              {formatMessage(messages.methodSelectionTitle)}
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    cursor: 'pointer',
                    border: 'none',
                    boxShadow: '0 0 0 1px rgba(0,0,0,0.12)',
                    transition: 'box-shadow 0.2s, border-color 0.2s',
                    '&:hover': {
                      boxShadow: theme =>
                        `0 0 0 2px ${theme.palette.primary.main}, 0 0 10px 0 rgba(0,0,0,0.2)`,
                    },
                  }}
                  onClick={() => handleMethodSelect('totp')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Smartphone sx={{ mr: 2, color: 'primary.main' }} />
                    <Typography variant="h6">{formatMessage(messages.totpOption)}</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {formatMessage(messages.totpDescription)}
                  </Typography>
                  <Button variant="text" color="primary">
                    <FormattedMessage id="Common.Select" defaultMessage="Select" />
                  </Button>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    cursor: 'pointer',
                    border: 'none',
                    boxShadow: '0 0 0 1px rgba(0,0,0,0.12)',
                    transition: 'box-shadow 0.2s, border-color 0.2s',
                    '&:hover': {
                      boxShadow: theme =>
                        `0 0 0 2px ${theme.palette.primary.main}, 0 0 10px 0 rgba(0,0,0,0.2)`,
                    },
                  }}
                  onClick={() => handleMethodSelect('passkey')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Key sx={{ mr: 2, color: 'primary.main' }} />
                    <Typography variant="h6">{formatMessage(messages.passkeyOption)}</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {formatMessage(messages.passkeyDescription)}
                  </Typography>
                  <Button variant="text" color="primary">
                    <FormattedMessage id="Common.Select" defaultMessage="Select" />
                  </Button>
                </Paper>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
        {selectedMethod === 'totp' && (
          <TwoFactorSetup
            open={setupOpen}
            onClose={handleClose}
            onComplete={handleComplete}
            onError={handleError}
          />
        )}

        {selectedMethod === 'passkey' && (
          <PasskeySetup
            open={setupOpen}
            onClose={handleClose}
            onComplete={handleComplete}
            onError={handleError}
          />
        )}
      </Box>
    </LayoutFocused>
  );
}

export default TwoFactorSetupPage;
